// The platform's exclusive-choice control (n39, 04 #39): the segmented chips Invite grew
// (mode / client type) and the joined mode pair Settings grew (margin / markup), unified
// into ONE shared component on the --seg-* theme-sheet tokens. Owner ruling: the selected
// fill is the PRIMARY pairing in both layouts (this also retired the four hard-coded
// white-text class sites in Settings).
//
// Two layouts:
//   chips  = full-width flex-1 segments with a gap (the Invite form shape)
//   joined = one fused control, segments divided by a hairline (the Settings mode shape)
// Semantics: role="radiogroup" with role="radio" buttons (aria-checked carries the
// selection; Left/Right/Up/Down move it). Use for a small exclusive set the user should
// see whole; keep <select> for long lists.

export interface SegmentedOption<V extends string> {
  value: V;
  label: string;
  disabled?: boolean;
}

interface SegmentedSelectProps<V extends string> {
  options: ReadonlyArray<SegmentedOption<V>>;
  value: V;
  onChange: (value: V) => void;
  layout?: 'chips' | 'joined';
  /** Accessible name for the group; supply it whenever no visible label element points here. */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function SegmentedSelect<V extends string>({
  options,
  value,
  onChange,
  layout = 'chips',
  ariaLabel,
  disabled = false,
  className = '',
}: SegmentedSelectProps<V>): JSX.Element {
  const move = (delta: number): void => {
    const enabled = options.filter((o) => !o.disabled);
    if (enabled.length === 0) return;
    const at = enabled.findIndex((o) => o.value === value);
    const next = enabled[(at + delta + enabled.length) % enabled.length];
    if (next && next.value !== value) onChange(next.value);
  };
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    }
  };
  const wrapper =
    layout === 'joined'
      ? 'inline-flex overflow-hidden rounded-[var(--btn-radius)] border border-[var(--seg-idle-edge)] divide-x divide-[var(--seg-idle-edge)]'
      : 'flex gap-2';
  const segment = (o: SegmentedOption<V>, selected: boolean): string =>
    [
      'cursor-pointer text-center transition control-weight text-sm',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent)]',
      layout === 'joined' ? 'px-4 py-1.5' : 'flex-1 rounded-[var(--btn-radius)] border px-4 py-2',
      selected
        ? [
            'bg-[var(--seg-selected-bg)] text-[var(--seg-selected-text)] ring-1 ring-inset ring-[var(--seg-selected-edge)]',
            layout === 'joined' ? '' : 'border-transparent',
          ].join(' ')
        : [
            'control-glass text-[var(--color-text)] hover:bg-[var(--seg-idle-hover-bg)]',
            layout === 'joined' ? '' : 'border-[var(--seg-idle-edge)]',
          ].join(' '),
      o.disabled || disabled ? 'cursor-not-allowed opacity-50' : '',
    ].join(' ');
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={[wrapper, className].join(' ')} onKeyDown={onKeyDown}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={o.disabled || disabled}
            className={segment(o, selected)}
            onClick={() => {
              if (!selected) onChange(o.value);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
